import {
  PicklistEventType,
  PointAndPlaneFragment,
} from '@warebee/frontend/data-access-api-graphql';
import { ActualityHqDataRow } from '@warebee/shared/dataset-queries';
import Konva from 'konva';
import { KonvaEventObject } from 'konva/lib/Node';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import {
  Circle,
  Group,
  Image as KonvaImage,
  Label,
  Tag,
  Text,
} from 'react-konva';
import { useSetRecoilState } from 'recoil';
import { TwTheme } from '../../../Tw';
import { getBestContrastColor } from '../../common/color.helper';
import useGetNamedColor from '../../common/useGetNamedColor';
import * as Icon from '../../components/icons';
import {
  ActualityAggregationColorBy,
  ActualityAggregationField,
} from '../../feed/store/actuality.kpi.types';
import { AgentCategoryId } from '../../resourcePolicy/agentData/agent.types';
import { getAgentIcon } from '../../resourcePolicy/agentData/agentDataHelper';
import { viewerSelectedLocationIdWithToggle } from '../viewer/store/viewer.state';
import {
  getEventCircleStyle,
  getSideOffset,
  RouteSide,
} from './route/sideOffset';

const colors = TwTheme.extend.colors;

export type EventFeatureProps = {
  // points: PointAndPlaneFragment[];
  scale?: number;
  event: ActualityHqDataRow;
  icon?: React.ReactNode | AgentCategoryId;
  colorCircle?: string;
  colorIcon?: string;
  colorSelection?: string;
  iconRotation?: number;
  animate?: boolean;
  isSelected?: boolean;
  selectedAnimation?: 'marching-ants' | 'none';
  title?: string;
  subtitle?: string;
  showLabels?: boolean;
  tooltipData?: Record<string, string | number>;
  showTooltip?: boolean;
  colorFields?: ActualityAggregationField[];
  supportedColorBy?: ActualityAggregationColorBy[];
  baseOpacity?: number;
  dimmedOpacity?: number;
  onHover?: (eventId: string | null) => void;
  onSelection?: (eventId: string | null) => void;
  circleRadius?: number;
  showStartCircle?: boolean;
  startCircleRadius?: number;
  startCircleColor?: string;
  startCircleOpacity?: number;
  eventType?: PicklistEventType | string;
  animateStartEnd?: boolean;
};

const EventFeature: React.FC<EventFeatureProps> = props => {
  const {
    // points,
    event,
    icon,
    // colorCircle,
    colorIcon,
    iconRotation,
    animate,
    isSelected,
    selectedAnimation,
    title,
    subtitle,
    showLabels,
    tooltipData,
    showTooltip,
    baseOpacity = 0.85,
    dimmedOpacity = 0.3,
    onHover,
    onSelection,
    circleRadius = 60,
    showStartCircle = false,
    startCircleRadius = 40,
    startCircleColor,
    startCircleOpacity = 0.2,
    eventType,
    animateStartEnd = false,
  } = props;

  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [startImage, setStartImage] = useState<HTMLImageElement | null>(null);
  const [endImage, setEndImage] = useState<HTMLImageElement | null>(null);
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    text: '',
  });

  const startPingRef = useRef<Konva.Circle>(null);
  const endPingRef = useRef<Konva.Circle>(null);

  const setSelectedLocation = useSetRecoilState(
    viewerSelectedLocationIdWithToggle,
  );

  const sonarRef = useRef<Konva.Circle>(null);
  const marchingAntsRef = useRef<Konva.Circle>(null);
  const getNamedColor = useGetNamedColor();
  // Use shared offset logic
  const side = (event.locationSide || undefined) as RouteSide;
  const xOffset = getSideOffset(side, false);
  const yOffset = getSideOffset(side, false);
  const position = { x: event.x, y: event.y }; // + xOffset, y: event.y + yOffset

  // TODO: move to RouteFeatureLight (last travel segment)
  // const lineConfig: LineConfig = {
  //   points: _(event.route.waypoints.)
  //     .compact()
  //     .map(p => [p., p.y])
  //     .flatten()
  //     .value(),
  //   stroke: routeColors.line,
  //   strokeWidth: 12,
  //   closed: false,
  //   dash: [24, 48],
  //   opacity: 0.5,
  //   lineCap: 'round',
  //   lineJoin: 'round',
  //   globalCompositeOperation: 'multiply',
  // };

  const IconComponent = useMemo(() => {
    if (!icon) return null;
    if (typeof icon === 'string') {
      return getAgentIcon(icon as AgentCategoryId);
    }
    return icon;
  }, [icon]);

  // Create function to render icon to image
  const renderIconToImage = (
    IconComponent: React.ComponentType | React.ReactElement,
    color: string,
    onLoad: (img: HTMLImageElement) => void,
  ) => {
    try {
      let iconElement: React.ReactElement;

      if (typeof IconComponent === 'function') {
        iconElement = React.createElement(IconComponent);
      } else {
        iconElement = IconComponent as React.ReactElement;
      }

      const svgString = ReactDOMServer.renderToString(iconElement);
      const dataUrl = `data:image/svg+xml;base64,${btoa(svgString)}`;
      const img = new window.Image();
      img.src = dataUrl;
      img.onload = () => {
        // console.log('Icon loaded successfully:', IconComponent);
        onLoad(img);
      };
      img.onerror = error => {
        console.error('Failed to load icon image:', error);
      };
    } catch (error) {
      console.warn('Failed to render icon:', error);
    }
  };

  useEffect(() => {
    if (!IconComponent) {
      setImage(null);
      return;
    }

    // Only render if IconComponent is a React component, not a number
    if (
      typeof IconComponent === 'function' ||
      React.isValidElement(IconComponent)
    ) {
      renderIconToImage(
        IconComponent,
        colorIcon || colors.ActivityEventProcessType.START,
        setImage,
      );
    }
  }, [IconComponent, colorIcon]);

  useEffect(() => {
    let anim: Konva.Animation | null = null;
    if (animate && sonarRef.current) {
      anim = new Konva.Animation(frame => {
        if (!frame) return;
        const period = 2000;
        const scale = 1 + ((frame.time % period) / period) * 2.5;
        const opacity = 1 - (frame.time % period) / period;
        sonarRef.current?.scaleX(scale);
        sonarRef.current?.scaleY(scale);
        sonarRef.current?.opacity(opacity);
      }, sonarRef.current.getLayer());
      anim.start();
    }
    return () => {
      anim?.stop();
    };
  }, [animate]);

  useEffect(() => {
    let anim: Konva.Animation | null = null;
    if (
      isSelected &&
      selectedAnimation === 'marching-ants' &&
      marchingAntsRef.current
    ) {
      // Use same animation approach as AnimatedArrow
      const dashSize = 4 * 3.5;
      const dashSpace = dashSize * 4;
      const fps = 60;

      anim = new Konva.Animation(frame => {
        if (!frame) return;
        const dashOffset =
          -((frame.time / 1000) * fps) % (dashSpace * (dashSize + dashSpace));
        marchingAntsRef.current?.dashOffset(dashOffset);
      }, marchingAntsRef.current.getLayer());
      anim.start();
    }
    return () => {
      anim?.stop();
    };
  }, [isSelected, selectedAnimation]);

  const handleMouseEnter = (e: KonvaEventObject<MouseEvent>) => {
    onHover?.(event.eventId);

    if (!showTooltip || !tooltipData) return;
    const stage = e.target.getStage();
    if (!stage) return;

    const text = Object.entries(tooltipData)
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n');

    setTooltip({
      visible: true,
      x: e.target.x() + 120,
      y: e.target.y(),
      text,
    });
  };

  const handleMouseLeave = () => {
    onHover?.(null);

    if (!showTooltip) return;
    setTooltip({ visible: false, x: 0, y: 0, text: '' });
  };

  const handleClick = () => {
    console.log(
      'EventFeature clicked:',
      event.eventId,
      'isSelected:',
      isSelected,
    );
    onSelection?.(isSelected ? null : event.eventId);
  };

  // Positioning logic based on present elements
  const hasIcon = !!image;
  const hasTitle = !!(showLabels && title);
  const hasSubtitle = !!(showLabels && subtitle);

  // Calculate icon size based on presence of text elements
  const iconSize = hasTitle || hasSubtitle ? 96 : 128;

  const eventColors = getNamedColor(props.event['agentId']);
  const eventColor = eventColors?.[0];

  // Get circle style based on event type
  const circleStyle = getEventCircleStyle(
    eventType as PicklistEventType,
    eventColor,
  );

  const startColor = startCircleColor || eventColor || colors.route.line;

  const firstJobEvent = _.isNil(event.previousJobActivityTime);
  const firstPoint = (
    event.route as any as PointAndPlaneFragment[][]
  )?.[0]?.[0];

  // followingEmployeeJobActivityTime
  const lastJobEvent = _.isNil(event.followingJobActivityTime);
  const lastPoint = _.last(
    _.last(event.route as any as PointAndPlaneFragment[][]),
  );

  // Check if firstPoint and lastPoint are at the same location
  const sameLocation = useMemo(() => {
    if (!firstPoint || !lastPoint) return false;
    return firstPoint.x === lastPoint.x && firstPoint.y === lastPoint.y;
  }, [firstPoint, lastPoint]);

  // Render start/end icons
  useEffect(() => {
    if (sameLocation && (firstJobEvent || lastJobEvent)) {
      // Use icon when points are at same location
      console.log('Using CircleWaypoint4 for same location');
      // renderIconToImage(Icon.CircleWaypoint4, '#FFFFFF', setStartImage);
      renderIconToImage(Icon.CircleWaypoint4, '#FFFFFF', setEndImage);
    } else {
      if (firstJobEvent) {
        console.log('Using CircleWaypoint4 for start');
        renderIconToImage(Icon.CircleWaypoint4, '#FFFFFF', setStartImage);
      }
      if (lastJobEvent) {
        console.log('Using CircleWaypoint4 for end');
        renderIconToImage(Icon.CircleWaypoint4, '#FFFFFF', setEndImage);
      }
    }
  }, [firstJobEvent, lastJobEvent, sameLocation]);

  // Start/End ping animation
  useEffect(() => {
    let startAnim: Konva.Animation | null = null;
    let endAnim: Konva.Animation | null = null;

    if (animateStartEnd) {
      // Start circle ping animation
      if (firstJobEvent && firstPoint && startPingRef.current) {
        startAnim = new Konva.Animation(frame => {
          if (!frame) return;
          const period = 100000;
          const scale = 1 + ((frame.time % period) / period) * 1.4;
          const opacity = 0.1 - ((frame.time % period) / period) * 0.2;
          startPingRef.current?.scaleX(scale);
          startPingRef.current?.scaleY(scale);
          startPingRef.current?.opacity(opacity);
        }, startPingRef.current.getLayer());
        startAnim.start();
      }

      // End circle ping animation
      if (lastJobEvent && lastPoint && !sameLocation && endPingRef.current) {
        endAnim = new Konva.Animation(frame => {
          if (!frame) return;
          const period = 1500;
          const scale = 1 + ((frame.time % period) / period) * 1.8;
          const opacity = 0.8 - ((frame.time % period) / period) * 0.8;
          endPingRef.current?.scaleX(scale);
          endPingRef.current?.scaleY(scale);
          endPingRef.current?.opacity(opacity);
        }, endPingRef.current.getLayer());
        endAnim.start();
      }
    }

    return () => {
      startAnim?.stop();
      endAnim?.stop();
    };
  }, [
    animateStartEnd,
    firstJobEvent,
    lastJobEvent,
    firstPoint,
    lastPoint,
    sameLocation,
  ]);

  // Calculate start circle position (offset from main circle)
  const startCircleOffset = circleRadius; // 10px gap
  const startPosition = {
    x: position.x - startCircleOffset,
    y: position.y,
  };

  // Calculate text positions and sizing based on what's present
  const getTextPositions = () => {
    if (hasTitle && hasSubtitle) {
      // Both title and subtitle present
      if (hasIcon) {
        // All 3 elements - use current positioning
        return {
          titleY: 50,
          subtitleY: -70,
        };
      } else {
        // Only title and subtitle - center them
        return {
          titleY: -30,
          subtitleY: 10,
        };
      }
    } else if (hasTitle || hasSubtitle) {
      // Only one text element - center it
      return {
        titleY: -10,
        subtitleY: -10,
      };
    }
    return { titleY: 0, subtitleY: 0 };
  };

  // Calculate optimal font size and truncated text
  const getTextConfig = (
    text: string,
    maxWidth: number,
    maxFontSize: number = 32,
  ) => {
    if (!text) return { fontSize: maxFontSize, truncatedText: '' };

    // Start with max font size and work down
    for (let fontSize = maxFontSize; fontSize >= 8; fontSize -= 2) {
      // Rough estimation: character width is about 0.6 * fontSize
      const estimatedWidth = text.length * fontSize * 0.6;

      if (estimatedWidth <= maxWidth) {
        return { fontSize, truncatedText: text };
      }
    }

    // If even smallest font doesn't fit, truncate the text
    const minFontSize = 8;
    const maxChars = Math.floor(maxWidth / (minFontSize * 0.6)) - 3; // -3 for "..."
    const truncatedText =
      text.length > maxChars ? text.substring(0, maxChars) + '...' : text;

    return { fontSize: minFontSize, truncatedText };
  };

  const { titleY, subtitleY } = getTextPositions();
  const rotation = iconRotation !== undefined ? iconRotation : 180;

  // Calculate text area width (80% of circle diameter for padding)
  const textAreaWidth = circleRadius * 1.6;

  // Get optimized text configs
  const titleConfig = getTextConfig(title || '', textAreaWidth, 24);
  const subtitleConfig = getTextConfig(subtitle || '', textAreaWidth, 32);

  // Define smaller radius and styling for start/end circles
  const startEndCircleRadius = 50;
  const iconSizeRatio = 1.5;
  const startEndCircleStyle = {
    fill: sameLocation
      ? colors.ActivityEventProcessType.START
      : firstJobEvent
        ? colors.ActivityEventProcessType.START
        : colors.ActivityEventProcessType.START,
    stroke: sameLocation
      ? colors.ActivityEventProcessType.START
      : firstJobEvent
        ? colors.ActivityEventProcessType.START
        : colors.ActivityEventProcessType.START,
    strokeWidth: 0,
    opacity: 0.5,
  };

  return (
    <Group>
      {/* Start Circle */}
      {firstJobEvent && firstPoint && (
        <Group>
          <Circle
            x={firstPoint.x}
            y={firstPoint.y}
            radius={startEndCircleRadius}
            fill={sameLocation ? circleStyle.fill : circleStyle.fill}
            // stroke={
            //   sameLocation
            //     ? startEndCircleStyle.stroke
            //     : colors.ActivityEventProcessType.START
            // }
            // strokeWidth={startEndCircleStyle.strokeWidth}
            opacity={startEndCircleStyle.opacity}
            listening={false}
          />
          {/* Start ping animation */}
          {animateStartEnd && (
            <Circle
              ref={startPingRef}
              x={firstPoint.x}
              y={firstPoint.y}
              radius={startEndCircleRadius}
              fill={colors.ActivityEventProcessType.START}
              opacity={0.2}
              listening={false}
            />
          )}
          {startImage && (
            <KonvaImage
              image={startImage}
              x={firstPoint.x}
              y={firstPoint.y}
              width={startEndCircleRadius * iconSizeRatio}
              height={startEndCircleRadius * iconSizeRatio}
              offsetX={(startEndCircleRadius * iconSizeRatio) / 2}
              offsetY={(startEndCircleRadius * iconSizeRatio) / 2}
              rotation={0}
              listening={false}
            />
          )}
        </Group>
      )}

      {lastJobEvent && lastPoint && !sameLocation && (
        <Group>
          <Circle
            x={lastPoint.x}
            y={lastPoint.y}
            radius={startEndCircleRadius}
            fill={circleStyle.fill}
            // stroke={colors.ActivityEventProcessType.END}
            // strokeWidth={startEndCircleStyle.strokeWidth}
            opacity={startEndCircleStyle.opacity}
            listening={false}
          />
          {/* End ping animation */}
          {animateStartEnd && (
            <Circle
              ref={endPingRef}
              x={lastPoint.x}
              y={lastPoint.y}
              radius={startEndCircleRadius}
              fill={colors.ActivityEventProcessType.END}
              opacity={0.2}
              listening={false}
            />
          )}
          {endImage && (
            <KonvaImage
              image={endImage}
              x={lastPoint.x}
              y={lastPoint.y}
              width={startEndCircleRadius * iconSizeRatio}
              height={startEndCircleRadius * iconSizeRatio}
              offsetX={(startEndCircleRadius * iconSizeRatio) / 2}
              offsetY={(startEndCircleRadius * iconSizeRatio) / 2}
              rotation={0}
              listening={false}
            />
          )}
        </Group>
      )}

      {/* Main Circle */}
      <Circle
        x={position.x}
        y={position.y}
        radius={circleStyle.radius}
        fill={circleStyle.fill}
        stroke={circleStyle.stroke}
        strokeWidth={circleStyle.strokeWidth}
        dash={circleStyle.isDashed ? circleStyle.dashPattern : undefined}
        opacity={circleStyle.opacity}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onTap={handleClick}
        listening={true}
        cursor="pointer"
      />

      {/* Animate (sonar) Circle */}
      {animate && (
        <Circle
          ref={sonarRef}
          x={position.x}
          y={position.y}
          radius={circleStyle.radius}
          fill={circleStyle.fill}
          opacity={0.5}
          listening={false}
        />
      )}

      {/* Marching ants selection circles */}
      {isSelected && selectedAnimation === 'marching-ants' && (
        <>
          {/* Static border circle - same as AnimatedArrow approach */}
          <Circle
            x={position.x}
            y={position.y}
            radius={circleStyle.radius + 8}
            stroke={colors.route.selectedBorder}
            strokeWidth={12}
            listening={false}
          />
          {/* Animated dashed border circle */}
          <Circle
            ref={marchingAntsRef}
            x={position.x}
            y={position.y}
            radius={circleStyle.radius + 8}
            stroke={colors.route.selectedBorderDash}
            strokeWidth={12}
            dash={[24, 24]}
            listening={false}
          />
        </>
      )}

      {/* Icon */}
      {image && (
        <KonvaImage
          image={image}
          x={position.x}
          y={position.y}
          width={iconSize}
          height={iconSize}
          offsetX={iconSize / 2.2}
          offsetY={iconSize / 2.2}
          rotation={rotation}
          listening={false}
        />
      )}

      {/* Labels */}
      {showLabels && (
        <Group x={position.x} y={position.y} scaleX={1} scaleY={-1}>
          {title && (
            <Text
              text={titleConfig.truncatedText}
              x={-textAreaWidth / 2}
              y={titleY}
              fontSize={titleConfig.fontSize}
              fill={getBestContrastColor(circleStyle.fill)}
              align="center"
              width={textAreaWidth}
              wrap="none"
              ellipsis={true}
              listening={false}
            />
          )}
          {subtitle && (
            <Text
              text={subtitleConfig.truncatedText}
              x={-textAreaWidth / 2}
              y={subtitleY}
              fontSize={subtitleConfig.fontSize}
              fill={getBestContrastColor(circleStyle.fill)}
              align="center"
              width={textAreaWidth}
              fontFamily={'NeoSans-Regular'}
              wrap="none"
              ellipsis={true}
              listening={false}
            />
          )}
        </Group>
      )}

      {/* Tooltip */}
      {showTooltip && tooltip.visible && (
        <Label x={tooltip.x} y={tooltip.y} scaleX={1} scaleY={-1}>
          <Tag
            fill={'black'}
            pointerDirection={'left'}
            lineJoin={'round'}
            shadowColor={'black'}
            shadowBlur={10}
            shadowOpacity={0.5}
          />
          <Text
            text={tooltip.text}
            fontFamily={'NeoSans-Regular'}
            fontSize={18}
            padding={5}
            fill={'white'}
          />
        </Label>
      )}
    </Group>
  );
};

export default React.memo(EventFeature);
